import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MessageCircle, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useProductModal } from "@/context/ProductModalContext";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ANIMAL_LABELS,
  CATEGORY_LABELS,
  formatPrice, effectivePrice,
  type Product,
} from "@/data/products";
import { AnimalIcon, CategoryIcon } from "@/components/icons/BrandIcons";
import { useBooking } from "@/store/booking";
import { cn } from "@/lib/utils";

export function ProductModal() {
  const { currentProduct, closeProductModal } = useProductModal();
  const isMobile = useIsMobile();
  const open = Boolean(currentProduct);

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(v) => !v && closeProductModal()}>
        <SheetContent
          side="bottom"
          className="p-0 max-h-[92vh] rounded-t-2xl overflow-hidden bg-brand-cream border-brand-gold/30"
        >
          <SheetTitle className="sr-only">
            {currentProduct?.name ?? "Prodotto"}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Dettagli prodotto
          </SheetDescription>
          {currentProduct && (
            <ModalBody product={currentProduct} variant="mobile" />
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeProductModal()}>
      <DialogContent className="p-0 max-w-[920px] w-[92vw] max-h-[88vh] overflow-hidden bg-brand-cream border-brand-gold/30 sm:rounded-lg">
        <DialogTitle className="sr-only">
          {currentProduct?.name ?? "Prodotto"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Dettagli prodotto
        </DialogDescription>
        {currentProduct && (
          <ModalBody product={currentProduct} variant="desktop" />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModalBody({
  product,
  variant,
}: {
  product: Product;
  variant: "desktop" | "mobile";
}) {
  const gallery = product.gallery ?? [product.image];
  const [active, setActive] = useState(0);
  const { closeProductModal } = useProductModal();
  const add = useBooking((s) => s.add);
  const navigate = useNavigate();
  const isBookable = product.bookable !== false;

  // Reset gallery on product change
  useEffect(() => {
    setActive(0);
  }, [product.slug]);

  const onPrimary = () => {
    if (isBookable) {
      add({
        slug: product.slug,
        name: product.name,
        price: effectivePrice(product),
        unit: product.unit,
        image: product.image,
      });
      toast.success("Aggiunto al carrello richieste", { description: product.name });
      closeProductModal();
    } else {
      closeProductModal();
      navigate({ to: "/contatti" });
    }
  };

  if (variant === "desktop") {
    return (
      <div className="grid grid-cols-2 max-h-[88vh]">
        {/* LEFT */}
        <div className="bg-brand-cream-dark/40 p-6 flex flex-col gap-4 overflow-hidden">
          <div className="aspect-square overflow-hidden rounded-md bg-white shadow-[var(--shadow-soft)]">
            <AnimatePresence mode="wait">
              <motion.img
                key={gallery[active]}
                src={gallery[active]}
                alt={product.name}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full h-full object-cover"
              />
            </AnimatePresence>
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={cn(
                    "w-[60px] h-[60px] overflow-hidden rounded-md border-2 transition-all",
                    i === active
                      ? "border-brand-red"
                      : "border-brand-cream-dark opacity-60 hover:opacity-100"
                  )}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="overflow-y-auto p-7 flex flex-col gap-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-brand-gold">
            {CATEGORY_LABELS[product.category]}
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-brand-charcoal leading-tight">
            {product.name}
          </h2>
          <IconRow product={product} large />
          <p className="text-brand-warm-gray font-italic-serif italic text-base leading-relaxed">
            {product.shortDescription}
          </p>
          <p className="text-sm text-brand-charcoal/80 leading-[1.75] line-clamp-5">
            {product.longDescription[0]}
          </p>
          <Link
            to="/prodotti/$slug"
            params={{ slug: product.slug }}
            onClick={closeProductModal}
            className="self-start text-xs uppercase tracking-[0.18em] text-brand-red hover:underline"
          >
            Continua a leggere →
          </Link>

          <div className="gold-divider my-1" />

          <div>
            <div className="font-numeric font-bold text-brand-red text-3xl md:text-4xl leading-none">
              {formatPrice(product)}
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-brand-warm-gray mt-1.5">
              {product.unit === "kg" ? "Prezzo al kg" : "Prezzo al pezzo"}
            </div>
            <div className="text-xs text-brand-warm-gray mt-1.5">
              Allergeni e origine della carne: te li indichiamo al banco o quando ti richiamiamo.
            </div>
          </div>

          <div className="flex flex-col gap-2.5 mt-1">
            <button
              type="button"
              onClick={onPrimary}
              className="w-full inline-flex items-center justify-center gap-3 bg-brand-red hover:bg-brand-red-hover text-white px-6 py-4 rounded-md text-sm uppercase tracking-[0.18em] font-medium transition-colors"
            >
              {isBookable ? (
                <>
                  <ShoppingBag size={16} /> Aggiungi alla richiesta
                </>
              ) : (
                <>
                  <MessageCircle size={16} /> Richiedi ora
                </>
              )}
              <ArrowRight size={16} />
            </button>
            <Link
              to="/prodotti/$slug"
              params={{ slug: product.slug }}
              onClick={closeProductModal}
              className="w-full inline-flex items-center justify-center gap-2 border border-brand-charcoal/30 hover:border-brand-red hover:text-brand-red text-brand-charcoal px-6 py-3 rounded-md text-xs uppercase tracking-[0.18em] transition-colors"
            >
              Vedi la scheda completa →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // MOBILE
  return (
    <div className="flex flex-col h-full max-h-[92vh]">
      <div className="mx-auto mt-2 mb-1 h-1.5 w-12 rounded-full bg-brand-cream-dark" />
      <div className="overflow-y-auto flex-1 pb-28">
        <div className="aspect-[4/3] overflow-hidden bg-brand-cream-dark">
          <AnimatePresence mode="wait">
            <motion.img
              key={gallery[active]}
              src={gallery[active]}
              alt={product.name}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full object-cover"
            />
          </AnimatePresence>
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 px-4 mt-3 overflow-x-auto">
            {gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={cn(
                  "shrink-0 w-[56px] h-[56px] overflow-hidden rounded-md border-2",
                  i === active
                    ? "border-brand-red"
                    : "border-brand-cream-dark opacity-60"
                )}
              >
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="px-5 pt-5 flex flex-col gap-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-brand-gold">
            {CATEGORY_LABELS[product.category]}
          </div>
          <h2 className="font-display text-2xl text-brand-charcoal leading-tight">
            {product.name}
          </h2>
          <IconRow product={product} />
          <p className="text-brand-warm-gray font-italic-serif italic leading-relaxed">
            {product.shortDescription}
          </p>
          <p className="text-sm text-brand-charcoal/80 leading-[1.75]">
            {product.longDescription[0]}
          </p>
          <Link
            to="/prodotti/$slug"
            params={{ slug: product.slug }}
            onClick={closeProductModal}
            className="self-start text-xs uppercase tracking-[0.18em] text-brand-red"
          >
            Vedi scheda completa →
          </Link>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-brand-cream border-t border-brand-charcoal/10 px-5 py-3 flex items-center justify-between gap-4 shadow-[0_-4px_20px_rgba(42,37,32,0.08)]">
        <div>
          <div className="font-numeric font-bold text-brand-red text-2xl leading-none">
            {formatPrice(product)}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-brand-warm-gray mt-1">
            {product.unit === "kg" ? "al kg" : "al pezzo"}
          </div>
        </div>
        <button
          type="button"
          onClick={onPrimary}
          className="inline-flex items-center justify-center gap-2 bg-brand-red text-white px-5 py-3 rounded-md text-xs uppercase tracking-[0.16em] font-medium min-h-[44px]"
        >
          {isBookable ? <ShoppingBag size={14} /> : <MessageCircle size={14} />}
          {isBookable ? "Aggiungi" : "Richiedi"}
        </button>
      </div>
    </div>
  );
}

function IconRow({ product, large }: { product: Product; large?: boolean }) {
  const size = large ? 40 : 32;
  return (
    <div className="flex items-center gap-3">
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center justify-center text-brand-red">
            <AnimalIcon animal={product.animal} size={size} strokeWidth={1.8} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{ANIMAL_LABELS[product.animal]}</TooltipContent>
      </Tooltip>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center justify-center text-brand-green">
            <CategoryIcon category={product.category} size={size} strokeWidth={1.8} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{CATEGORY_LABELS[product.category]}</TooltipContent>
      </Tooltip>
    </div>
  );
}